import { useState } from "react";
import {
  Paper,
  Title,
  Text,
  Divider,
  Alert,
  SimpleGrid,
  Stack,
  UnstyledButton,
  Collapse,
  Group,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import type {
  FormSection,
  FieldElement,
  StructuralElement,
  LookupFieldType,
  LookupOption,
} from "@livingsyncs/types";
import { isFieldVisible } from "./engines/ConditionEngine";
import { FieldRenderer } from "./FieldRenderer";

interface SectionRendererProps {
  section: FormSection;
  /** Zero-based position — rendered as the "Section 01" eyebrow label. */
  sectionIndex?: number;
  values: Record<string, unknown>;
  onChange: (id: string, value: unknown) => void;
  readOnly: boolean;
  errors: Record<string, string[]>;
  /** Real, backend-sourced choices for entity-lookup field types — see FieldRenderer's lookupOptions doc. */
  lookupOptions?: Partial<Record<LookupFieldType, LookupOption[]>>;
  /** Lookup Drawer follow-up — see FieldRenderer's onFieldFocus doc. */
  onFieldFocus?: (fieldId: string) => void;
}

function StructuralElementRenderer({
  element,
}: {
  element: StructuralElement;
}) {
  switch (element.type) {
    case "header":
      return <Title order={3}>{element.content}</Title>;
    case "subheader":
      return <Title order={5}>{element.content}</Title>;
    case "paragraph":
      return <Text size="sm">{element.content}</Text>;
    case "divider":
      return <Divider />;
    case "alert":
      return (
        <Alert color="blue" variant="light">
          {element.content}
        </Alert>
      );
    default:
      return null;
  }
}

export function SectionRenderer({
  section,
  sectionIndex,
  values,
  onChange,
  readOnly,
  errors,
  lookupOptions,
  onFieldFocus,
}: SectionRendererProps) {
  const [collapsed, setCollapsed] = useState(false);

  // Hide the whole section if its visibleIf condition is false
  if (!isFieldVisible(section.visibleIf, values)) return null;

  const cols = section.columns ?? 1;
  const eyebrow =
    sectionIndex !== undefined
      ? `Section ${String(sectionIndex + 1).padStart(2, "0")}`
      : null;

  const content = (
    <SimpleGrid cols={cols} spacing="md">
      {section.elements.map((element) => {
        // Structural elements span full width regardless of column layout
        if (element.kind === "structural") {
          return (
            <Stack key={element.id} style={{ gridColumn: `1 / -1` }}>
              <StructuralElementRenderer element={element} />
            </Stack>
          );
        }

        // Field elements — hide when visibleIf is false
        const field = element as FieldElement;
        if (!isFieldVisible(field.visibleIf, values)) return null;

        const fieldErrors = errors[field.id];
        const errorMsg = fieldErrors?.[0];

        const fullWidth = field.layout?.fullWidth ?? false;

        return (
          <div
            key={field.id}
            style={fullWidth ? { gridColumn: `1 / -1` } : undefined}
          >
            <FieldRenderer
              element={field}
              value={values[field.id]}
              onChange={onChange}
              readOnly={readOnly}
              error={errorMsg}
              values={values}
              lookupOptions={lookupOptions}
              onFieldFocus={onFieldFocus}
            />
          </div>
        );
      })}
    </SimpleGrid>
  );

  const headerBody = (
    <div>
      {eyebrow && <Text className="ls-form-section-eyebrow">{eyebrow}</Text>}
      <Title order={4} className="ls-form-section-title" mt={eyebrow ? 4 : 0}>
        {section.title}
      </Title>
      {section.description && !(section.collapsible && collapsed) && (
        <Text size="sm" c="dimmed" mt={4}>
          {section.description}
        </Text>
      )}
    </div>
  );

  return (
    <Paper p="lg" radius="md" className="ls-form-section" withBorder={false}>
      <Stack gap="md">
        {/* Section header */}
        {section.collapsible ? (
          <UnstyledButton
            onClick={() => setCollapsed((c) => !c)}
            w="100%"
            className={collapsed ? undefined : "ls-form-section-header"}
          >
            <Group justify="space-between" wrap="nowrap">
              {headerBody}
              {collapsed ? (
                <IconChevronDown size={18} />
              ) : (
                <IconChevronUp size={18} />
              )}
            </Group>
          </UnstyledButton>
        ) : (
          <div className="ls-form-section-header">{headerBody}</div>
        )}

        {/* Fields */}
        {section.collapsible ? (
          <Collapse expanded={!collapsed}>{content}</Collapse>
        ) : (
          content
        )}
      </Stack>
    </Paper>
  );
}
